/**
 * Mantrana AI — bilingual translations (English + Hindi / Devanagari).
 *
 * Design principles for Hindi copy (per user brief):
 *  - simple, spoken, conversational Hindi (NOT Sanskrit-heavy, NOT bureaucratic)
 *  - emotionally intelligent, warm, premium
 *  - highly readable
 *  - NEVER Hinglish
 *  - archetype NAMES stay in English; everything else must be Hindi
 *
 * Usage:
 *   import { useLang } from "@/lib/i18n";
 *   const { lang, setLang, t } = useLang();
 *   t("hero.headline")
 *
 * Falls back to English if a Hindi key is missing.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const LS_KEY = "mantrana_lang";

/* ---------------------------------------------------------------- *
 * DICTIONARY
 * ---------------------------------------------------------------- */
export const STRINGS = {
  en: {
    /* ─── Global ─── */
    "app.name": "Mantrana One",
    "brand.tagline": "Decision Intelligence · Astrology · Numerology · Psychology · AI",
    "brand.subline": "More than prediction. We help you understand yourself.",
    "nav.howItWorks": "How it Works",
    "nav.features": "Features",
    "nav.simulator": "Simulator",
    "nav.pricing": "Pricing",
    "nav.startReading": "Get My Clarity Report",
    "common.continue": "Continue",
    "common.back": "Back",
    "common.next": "Next",
    "common.previous": "Previous",
    "common.skip": "Skip",
    "common.retry": "Try again",
    "common.close": "Close",
    "common.language": "Language",
    "common.home": "Home",
    "common.returnHome": "Return to Home",
    "common.processing": "Processing…",
    "common.loading": "Loading…",

    /* ─── Language select ─── */
    "lang.title": "Choose your language",
    "lang.subtitle": "Your entire clarity journey — every question, every reading, every conversation — will unfold in the language you select.",
    "lang.en.label": "English",
    "lang.en.note": "Natural, warm English",
    "lang.hi.label": "हिन्दी",
    "lang.hi.note": "Pure Hindi, in Devanagari",
    "lang.continue": "Continue",
    "lang.change.later": "You can change this anytime from the top bar.",

    /* ─── Landing hero ─── */
    "hero.headline.a": "Life's biggest uncertainties and questions",
    "hero.headline.b": "deserve more than",
    "hero.headline.italic": "predictions.",
    "hero.subheadline": "Mantrana One combines psychology, behavioral science, ancient wisdom, spirituality and artificial intelligence to help you navigate life's questions with greater clarity and confidence.",
    "hero.supporting": "For career, relationships, money and life decisions.",
    "hero.positioning.a": "More than prediction.",
    "hero.positioning.b": "We help you understand yourself.",
    "hero.cta.start": "Get My Clarity Report",
    "hero.cta.watch": "Watch Demo",
    "hero.trust.personalised": "Personalised Guidance",
    "hero.trust.wisdom": "AI + Ancient Wisdom",
    "hero.trust.private": "Private & Secure",
    "hero.trust.psychology": "Behavioural Psychology",

    /* ─── Optional personal context ─── */
    "ctx.title": "What question is weighing on your mind?",
    "ctx.subtitle": "Share your situation if you'd like a more personal clarity reading.",
    "ctx.eyebrow": "Optional · deeply personal",
    "ctx.placeholder": "Tell us about your situation, confusion, or question.\n\nFor example:\n• Should I change my career?\n• Should I move to another city?\n• Should I continue this relationship?",
    "ctx.helper": "You may leave this blank.",
    "ctx.wordCount": "{n} / 250 words",
    "ctx.reveal": "Tell us what's on your mind",
    "ctx.hide": "Keep this reading un-personalized",

    /* ─── Landing sections ─── */
    "res.realQuestion": "Here's what we believe you're really asking",
    "features.eyebrow": "What we answer",
    "features.title.a": "The questions that keep",
    "features.title.b": "you up at night.",
    "feature.career.title": "Career Clarity",
    "feature.career.q": "Should I change my job?",
    "feature.relationship.title": "Relationship Guidance",
    "feature.relationship.q": "Why are we drifting apart?",
    "feature.finance.title": "Financial Decisions",
    "feature.finance.q": "Is this the right investment?",
    "feature.emotion.title": "Emotional Wellness",
    "feature.emotion.q": "Why am I feeling anxious?",
    "feature.spiritual.title": "Spiritual Guidance",
    "feature.spiritual.q": "What practices will help me?",
    "feature.purpose.title": "Life Purpose",
    "feature.purpose.q": "What am I meant to build?",

    /* ─── Approach section ─── */
    "approach.eyebrow": "Our method",
    "approach.title.a": "Not astrology.",
    "approach.title.b": "Clarity engineering.",
    "approach.body": "Mantrana fuses six disciplines into one quiet, intelligent companion — trained to listen to what you cannot say out loud, and respond with what you most need to hear.",
    "approach.item.1": "Vedic astrology — timing and life rhythms",
    "approach.item.2": "Numerology — innate patterns and tendencies",
    "approach.item.3": "Psychology — cognitive biases and emotion",
    "approach.item.4": "Behavioral science — habits and decisions",
    "approach.item.5": "Spiritual intelligence — meaning and depth",
    "approach.item.6": "AI decision support — clear, personal action",
    "approach.sample.eyebrow": "Sample reading",
    "approach.sample.clarity": "Clarity",
    "approach.sample.emotion.label": "Emotional state",
    "approach.sample.emotion.body": "You are operating from uncertainty rather than confidence.",
    "approach.sample.blind.label": "Hidden blocker",
    "approach.sample.blind.body": "Fear of failure is influencing the choices in front of you.",
    "approach.sample.action.label": "Immediate action",
    "approach.sample.action.body": "Tonight — write down the worst-case scenario in detail. Read it tomorrow.",

    /* ─── Growth / Clarity card & Simulator sections ─── */
    "growth.card.eyebrow": "The Clarity Card",
    "growth.card.title.a": "The most shared",
    "growth.card.title.b": "moment of clarity",
    "growth.card.title.c": "in India.",
    "growth.card.body": "Every reading generates a beautifully designed Clarity Card — your scores, your biggest blind spot, your recommended action — built to be sent. To the friend at 2am, to the cousin questioning their job, to anyone who needs to hear themselves clearly. Sharing is free, always.",
    "growth.card.stat.channels": "share channels",
    "growth.card.stat.shares": "shares per card",
    "growth.card.stat.free": "to share",
    "growth.card.cta": "Generate my Clarity Card",
    "growth.sim.eyebrow": "Life Decision Simulator",
    "growth.sim.title.a": "Three futures.",
    "growth.sim.title.b": "One decision.",
    "growth.sim.body": "Should you change jobs? Move cities? Get married? See three honest scenarios — Conservative, Balanced, Bold — with satisfaction probability, and emotional, financial and relationship impact for each.",
    "growth.sim.cta": "Try the Simulator",

    /* ─── Pricing (landing) ─── */
    "pricing.eyebrow": "Pricing",
    "pricing.title": "Begin with clarity.",
    "pricing.subtitle": "One free reading per identity. Then choose a guidance pack — pay only for what you ask.",
    "pricing.free.label": "Free reading",
    "pricing.free.oneEach": "One per unique identity",
    "pricing.free.item.1": "One full clarity reading",
    "pricing.free.item.2": "Personalized action plan",
    "pricing.free.item.3": "Shareable clarity card",
    "pricing.free.cta": "Start free",
    "pricing.recommended": "Recommended",
    "pricing.pack.oneQ": "1 Question",
    "pricing.pack.nQ": "{n} Questions",
    "pricing.pack.item.1": "Deep personalized guidance",
    "pricing.pack.item.2": "Decision analysis",
    "pricing.pack.item.3": "Emotional & behavioral mapping",
    "pricing.pack.item.4": "Premium clarity report",
    "pricing.pack.getOne": "Get 1 Question — ₹{price}",
    "pricing.pack.getN": "Get {n} Questions — ₹{price}",
    "footer.byline": "Clarity, engineered with reverence.",

    /* ─── Onboarding (Step 1) ─── */
    "onb.step1.eyebrow": "BIRTH DETAILS",
    "onb.step1.title.a": "Let's begin with",
    "onb.step1.title.b": "you.",
    "onb.step1.subtitle": "Your birth chart shapes the lens we use. This stays private.",
    "onb.fullName": "Full name",
    "onb.fullName.placeholder": "Your given name",
    "onb.dob": "Date of birth",
    "onb.tob": "Time of birth",
    "onb.pob": "Place of birth",
    "onb.pob.placeholder": "City, Country",
    "onb.gender": "Gender",
    "onb.gender.select": "Select",
    "onb.gender.male": "Male",
    "onb.gender.female": "Female",
    "onb.gender.other": "Other",
    "onb.gender.preferNot": "Prefer not to say",
    "onb.whatsapp": "WhatsApp number",
    "onb.whatsapp.forCode": "FOR CODE",
    "onb.whatsapp.placeholder": "e.g. +91 98765 43210",
    "onb.whatsapp.send": "Send code",
    "onb.whatsapp.sending": "Sending…",
    "onb.whatsapp.resend": "Resend code",
    "onb.whatsapp.enterOtp": "Enter the 6-digit code",
    "onb.whatsapp.verify": "Verify",
    "onb.whatsapp.verified": "WhatsApp verified",
    "onb.whatsapp.demoCode": "Demo mode — code",
    "onb.step.phase": "Phase 1 · Step {n} / 3",
    "onb.errors.missingBirth": "Please complete all birth details",
    "onb.errors.needCategory": "Please pick what's on your mind",
    "onb.errors.answerAll": "Please answer every question",

    /* ─── Onboarding (Step 2) — category ─── */
    "onb.step2.eyebrow": "WHAT'S ON YOUR MIND",
    "onb.step2.title.a": "What area of life",
    "onb.step2.title.b": "needs clarity?",
    "onb.step2.subtitle": "Pick one — we'll tune your reading around it.",
    "onb.returning.eyebrow": "Welcome back",
    "onb.returning.body.a": "We have your last",
    "onb.returning.body.b": "reading. Want a",
    "onb.returning.body.highlight": "60-second check-in",
    "onb.returning.body.c": "instead of the full 9 questions?",
    "onb.returning.quick": "Quick check-in",
    "onb.returning.full": "Full reading",
    "cat.Career": "Career",
    "cat.Relationships": "Relationships",
    "cat.Money": "Money",
    "cat.Family": "Family",
    "cat.Purpose": "Purpose",
    "cat.Major Decision": "Major Decision",

    /* ─── Onboarding (Step 3) — MCQs ─── */
    "onb.step3.eyebrow": "5-QUESTION DECISION PROFILE",
    "onb.step3.eyebrow.quick": "Quick check-in",
    "onb.step3.title": "Five gentle questions",
    "onb.step3.subtitle": "Each answers a different psychological axis — decision style, emotions, risk, motivation and inner conflict. Answer honestly; there is no right answer.",
    "onb.step3.progress": "Question {i} of {n}",
    "onb.step3.answered": "{a} of {n} answered",
    "onb.step3.answerMore": "Answer {n} more",
    "onb.step3.finish": "Reveal my clarity map",

    /* ─── Processing ─── */
    "proc.eyebrow": "Live · Tuning in",
    "proc.title.a": "Building your",
    "proc.title.b": "clarity map",
    "proc.personalize": "Personalizing insights for",
    "proc.phase1.title": "Reading your psychological decision profile",
    "proc.phase1.subtitle": "Turning your answers into a decision-style fingerprint",
    "proc.phase2.title": "Computing astrology & numerology signatures",
    "proc.phase2.subtitle": "Life path, expression, personal year and current transits",
    "proc.phase3.title": "Fusing psychology, astrology & numerology into one clarity map",
    "proc.phase3.subtitle": "Mantrana One is weaving all three signals into your unified report",
    "proc.status.now": "Now",
    "proc.status.done": "Done",
    "proc.retry.notice": "Retrying — the clarity engine hit a momentary snag…",
    "proc.elapsed": "elapsed · streaming live",
    "proc.phase.of": "Phase {i} of 3",
    "proc.footer.saved": "Your inputs are safely saved — nothing was lost",
    "proc.err.smallTitle": "Small hiccup",
    "proc.err.pauseTitle": "Temporary pause",
    "proc.err.back": "Back to details",
    "proc.numerology.lifePath": "Life Path",
    "proc.numerology.expression": "Expression",
    "proc.numerology.personalYear": "Personal Year",

    /* ─── Error copy per code ─── */
    "err.llm_budget_exceeded.title": "Our AI companion is topping up",
    "err.llm_budget_exceeded.body": "The clarity engine has hit its temporary daily limit. This will resolve in a few minutes — please try again shortly.",
    "err.llm_quota_exceeded.title": "AI companion catching its breath",
    "err.llm_quota_exceeded.body": "Our AI quota is momentarily exhausted. Give it a minute and try again — your inputs are safely saved.",
    "err.llm_auth_failed.title": "AI companion offline",
    "err.llm_auth_failed.body": "The clarity engine couldn't authenticate. Our team has been notified — please try again in a few minutes.",
    "err.llm_bad_json.title": "One retry away from clarity",
    "err.llm_bad_json.body": "The reading came back malformed. This usually resolves on a second attempt.",
    "err.llm_transient.title": "A momentary hiccup",
    "err.llm_transient.body": "Our AI companion was briefly overloaded. A fresh attempt usually goes through.",
    "err.llm_not_configured.title": "Configuration needed",
    "err.llm_not_configured.body": "The AI companion isn't configured on the server. Please contact support.",
    "err.network.title": "Connection dropped",
    "err.network.body": "We lost the connection to the clarity engine. Check your network and retry.",
    "err.llm_unknown.title": "Something unexpected happened",
    "err.llm_unknown.body": "The clarity engine hit an unusual snag. A retry usually fixes it — if not, please contact support.",

    /* ─── Results (global labels) ─── */
    "res.eyebrow": "YOUR CLARITY REPORT",
    "res.dci": "Decision Clarity Index",
    "res.mindSays": "What your mind is saying",
    "res.emotionsSay": "What your emotions are saying",
    "res.astrologySuggests": "What your astrology suggests",
    "res.numerologySuggests": "What your numerology suggests",
    "res.agreement": "Where all systems agree",
    "res.blindSpot": "Biggest blind spot",
    "res.ifWait": "If you wait",
    "res.ifAct": "If you act",
    "res.action": "Recommended action",
    "res.spiritual": "Spiritual practice",
    "res.spiritual.mantra": "Mantra",
    "res.spiritual.temple": "Temple",
    "res.spiritual.meditation": "Meditation",
    "res.spiritual.reflection": "Reflection",
    "res.spiritual.why": "Why this practice",
    "res.hiddenBlockers": "Hidden blockers",
    "res.confidence": "Confidence",
    "res.confidence.astro": "Astrological",
    "res.confidence.num": "Numerological",
    "res.confidence.behav": "Behavioral",
    "res.confidence.overall": "Overall",
    "res.dl.card": "Download Clarity Card",
    "res.share.card": "Share",
    "res.askMantra": "Ask MANTRA",
    "res.compareLast": "Compare to last reading",
    "res.emotionalConflict": "Emotional conflict",
    "res.hiddenFear": "Hidden fear",
    "res.decisionTension": "Decision tension",
    "res.hero.greeting": "{name}, here is what we see.",
    "res.loadError": "Could not load your reading.",
    "res.loading": "Loading your reading…",
    "res.notAvailable": "Not available.",

    /* ─── Radar axis names ─── */
    "radar.decision_confidence": "Decision Confidence",
    "radar.emotional_clarity": "Emotional Clarity",
    "radar.risk_readiness": "Risk Readiness",
    "radar.purpose_alignment": "Purpose Alignment",
    "radar.self_trust": "Self-Trust",

    /* ─── Paywall ─── */
    "pw.title": "You've reached the end of your current clarity journey",
    "pw.subtitle": "Continue with a deeper personalized guidance session.",
    "pw.premiumRequired": "Premium Required",
    "pw.pack.1q": "1 additional clarity question",
    "pw.pack.3q": "3 additional clarity questions",
    "pw.pack.get": "Get {label} — ₹{price}",
    "pw.previous.eyebrow": "Your last reading",
    "pw.previous.blurred": "Unlock more clarity to see the rest",
    "pw.teaser.90day": "{name}'s 90-day move",
    "pw.teaser.90day.you": "Your 90-day move",
    "pw.teaser.practice": "Your personal practice",
    "pw.teaser.nextStep": "Your decisive next step",
    "pw.teaser.blindSpot": "Your blind spot in full",
    "pw.teaser.unlock": "Unlock with a clarity pack",
    "pw.footer.note": "Your free clarity reading and follow-up question have been used. Continue with a paid clarity pack to unlock deeper guidance.",
    "pw.demo.note": "Demo checkout · payment integration ready",
    "pw.processing": "Processing…",

    /* ─── Clarity Card (share card) ─── */
    "card.section.eyebrow": "My Clarity Card",
    "card.section.title.a": "A keepsake of",
    "card.section.title.b": "today's truth.",
    "card.section.subtitle": "Share it. The friend you send this to is the one who needs it most right now.",
    "card.header": "Clarity Card",
    "card.for": "For",
    "card.archetype": "Archetype",
    "card.clarityScore": "Clarity Score",
    "card.emotional": "Emotional",
    "card.relationships": "Relationships",
    "card.blindSpot": "Biggest blind spot",
    "card.action": "Recommended action",
    "card.fallbackAction": "Pause. Breathe. Choose intentionally.",
    "card.share.whatsapp": "WhatsApp",
    "card.share.instagram": "Instagram",
    "card.share.download": "Download Card",
    "card.share.saved": "Card saved to your device",
    "card.share.downloadFailed": "Could not download the card",
    "card.share.instaSaved": "Card saved & caption copied — open Instagram to post",
    "card.share.privacy": "Every share carries your unique link · we honor your privacy",
    "card.caption": "My Mantrana One Clarity Card 🪔\nClarity Score: {clarity}/100\nEmotional: {emotional}/100 · Relationships: {relationship}/100\n\nBiggest blind spot: {blindSpot}\nNext move: {action}\n\nFind your clarity → {url}",

    /* ─── Floating Clarity Card (landing hero) ─── */
    "float.header": "Clarity Report",
    "float.blindSpot": "Primary Blind Spot",
    "float.focus": "Recommended Focus",
    "float.timing": "Timing",
    "float.confidence": "Confidence",

    /* ─── Chat (MANTRA) ─── */
    "chat.title": "Mantrana",
    "chat.subtitle": "your clarity companion",
    "chat.placeholder": "Ask Mantrana anything…",
    "chat.send": "Send",
    "chat.welcome": "I'm Mantrana — your personal clarity companion. Share a question or dilemma that's on your mind, and I'll respond with what you most need to hear.",
    "chat.locked": "You've used your free follow-up. Unlock more clarity to keep the conversation going.",
    "chat.thinking": "reflecting…",
    "chat.status.alwaysHere": "Always here",
    "chat.status.premium": "Premium required",
    "chat.status.remaining": "Questions remaining: {n}",
    "chat.balanceHint": "{n} paid question{s} remaining",
    "chat.unlockCta": "Unlock more questions",
    "chat.upsell.eyebrow": "Complimentary session complete",
    "chat.upsell.body": "That was your one complimentary clarity reading. Continue with a paid pack — deeper guidance, decision analysis and a premium clarity report for every question.",
    "chat.upsell.cta": "Choose a clarity pack",
    "chat.suggest.career.label": "Career confusion",
    "chat.suggest.career.q": "Should I change my career?",
    "chat.suggest.relationship.label": "Relationship dilemma",
    "chat.suggest.relationship.q": "Should I pursue this relationship?",
    "chat.suggest.finance.label": "Financial decision",
    "chat.suggest.finance.q": "How do I think clearly about this financial decision?",
    "chat.suggest.purpose.label": "Life purpose",
    "chat.suggest.purpose.q": "Why am I feeling stuck — what is blocking my growth?",
    "chat.paywall.title": "Your complimentary clarity reading is complete",
    "chat.paywall.subtitle": "Continue with a deeper personalized guidance session.",
    "chat.paywall.getShort": "Get {n}",
    "chat.paywall.oneQ": "Question",
    "chat.paywall.nQ": "Questions",

    /* ─── Simulator ─── */
    "sim.eyebrow": "Life Decision Simulator",
    "sim.title.a": "See three futures",
    "sim.title.b": "before you choose",
    "sim.title.italic": "one.",
    "sim.subtitle": "Ask any decision — career, love, money, geography. Mantrana renders three honest scenarios for you to walk through before committing.",
    "sim.placeholder": "Ask: should I…",
    "sim.button.simulate": "Simulate",
    "sim.button.simulating": "Simulating",
    "sim.err.short": "Phrase a real question first",
    "sim.err.fail": "Could not simulate. Try once more.",
    "sim.suggest.job": "Should I change my job?",
    "sim.suggest.business": "Should I start a business?",
    "sim.suggest.move": "Should I move cities?",
    "sim.suggest.marry": "Should I get married?",
    "sim.yourQuestion": "Your question",
    "sim.riskLabel": "{level} risk",
    "sim.risk.low": "Low",
    "sim.risk.medium": "Medium",
    "sim.risk.high": "High",
    "sim.probability": "Probability of satisfaction",
    "sim.of100": "/ 100",
    "sim.impact.emotional": "Emotional impact",
    "sim.impact.financial": "Financial impact",
    "sim.impact.relationship": "Relationship impact",
    "sim.recommended": "Recommended action",
    "sim.mantraTake": "Mantrana's take",
    "sim.getReading": "Get a full reading",
    "sim.askAnother": "Ask another",

    /* ─── Purchase / toast ─── */
    "purchase.success": "Purchase confirmed — {n} additional question(s) unlocked",
    "purchase.unlocked": "Unlocked — {n} question{s} remaining",
    "purchase.failed": "Payment failed. Please try again.",
    "purchase.verifyFailed": "Payment verification failed. Please contact support.",

    /* ─── Paywall pack features (localized bullets per pack) ─── */
    "pw.pack.pack_1.f1": "One additional question",
    "pw.pack.pack_1.f2": "Personalized clarity guidance",
    "pw.pack.pack_1.f3": "Decision analysis",
    "pw.pack.pack_1.f4": "Action recommendations",
    "pw.pack.pack_3.f1": "Three additional questions",
    "pw.pack.pack_3.f2": "Deeper clarity analysis",
    "pw.pack.pack_3.f3": "Behavioral insights",
    "pw.pack.pack_3.f4": "Action roadmap",
    "pw.pack.pack_3.f5": "Premium guidance",
    "pw.pack.default.f1": "Personalized clarity guidance",
    "pw.pack.default.f2": "Decision analysis",
    "pw.pack.default.f3": "Emotional & behavioral mapping",
    "pw.pack.default.f4": "Premium clarity report",
  },

  hi: {
    /* ─── Global ─── */
    "app.name": "मंत्रणा One",
    "brand.tagline": "निर्णय बुद्धिमत्ता · ज्योतिष · अंकशास्त्र · मनोविज्ञान · AI",
    "brand.subline": "सिर्फ भविष्यवाणी नहीं। हम आपको स्वयं को बेहतर समझने में मदद करते हैं।",
    "nav.howItWorks": "कैसे काम करता है",
    "nav.features": "विशेषताएँ",
    "nav.simulator": "सिम्युलेटर",
    "nav.pricing": "मूल्य",
    "nav.startReading": "मेरी क्लैरिटी रिपोर्ट पाएँ",
    "common.continue": "आगे बढ़ें",
    "common.back": "वापस",
    "common.next": "आगे",
    "common.previous": "पिछला",
    "common.skip": "छोड़ें",
    "common.retry": "फिर से कोशिश करें",
    "common.close": "बंद करें",
    "common.language": "भाषा",
    "common.home": "मुख्य पृष्ठ",
    "common.returnHome": "मुख्य पृष्ठ पर लौटें",
    "common.processing": "प्रोसेस हो रहा है…",
    "common.loading": "लोड हो रहा है…",

    /* ─── Language select ─── */
    "lang.title": "अपनी भाषा चुनें",
    "lang.subtitle": "आपकी पूरी स्पष्टता यात्रा — हर सवाल, हर रीडिंग, हर बातचीत — आपकी चुनी हुई भाषा में होगी।",
    "lang.en.label": "English",
    "lang.en.note": "स्वाभाविक, गर्मजोशी भरी अंग्रेज़ी",
    "lang.hi.label": "हिन्दी",
    "lang.hi.note": "शुद्ध हिन्दी, देवनागरी में",
    "lang.continue": "आगे बढ़ें",
    "lang.change.later": "आप इसे कभी भी ऊपर की पट्टी से बदल सकते हैं।",

    /* ─── Landing hero ─── */
    "hero.headline.a": "जीवन की सबसे बड़ी उलझनों और सवालों के लिए",
    "hero.headline.b": "केवल भविष्यवाणी",
    "hero.headline.italic": "काफी नहीं होती।",
    "hero.subheadline": "मंत्रणा One मनोविज्ञान, व्यवहार विज्ञान, प्राचीन ज्ञान, आध्यात्मिक समझ और कृत्रिम बुद्धिमत्ता को साथ लाकर जीवन के सवालों और उलझनों को अधिक स्पष्टता और आत्मविश्वास के साथ समझने में मदद करता है।",
    "hero.supporting": "करियर, रिश्ते, पैसे और जीवन के फैसलों के लिए।",
    "hero.positioning.a": "सिर्फ भविष्यवाणी नहीं।",
    "hero.positioning.b": "हम आपको स्वयं को बेहतर समझने में मदद करते हैं।",
    "hero.cta.start": "मेरी क्लैरिटी रिपोर्ट पाएँ",
    "hero.cta.watch": "डेमो देखें",
    "hero.trust.personalised": "व्यक्तिगत मार्गदर्शन",
    "hero.trust.wisdom": "AI और प्राचीन ज्ञान",
    "hero.trust.private": "गोपनीय और सुरक्षित",
    "hero.trust.psychology": "व्यवहार का मनोविज्ञान",

    /* ─── Optional personal context ─── */
    "ctx.title": "इस समय आपके मन में कौन-सा सवाल सबसे अधिक चल रहा है?",
    "ctx.subtitle": "यदि आप चाहें, तो अपनी परिस्थिति साझा करें। इससे आपकी रीडिंग अधिक व्यक्तिगत और उपयोगी हो सकेगी।",
    "ctx.eyebrow": "वैकल्पिक · गहराई से व्यक्तिगत",
    "ctx.placeholder": "अपने सवाल, परिस्थिति या मन की उलझन के बारे में लिखें।\n\nउदाहरण:\n• क्या मुझे नौकरी बदलनी चाहिए?\n• क्या मुझे दूसरे शहर जाना चाहिए?\n• क्या मुझे इस रिश्ते को आगे बढ़ाना चाहिए?",
    "ctx.helper": "यदि आप चाहें, तो इसे खाली छोड़ सकते हैं।",
    "ctx.wordCount": "{n} / 250 शब्द",
    "ctx.reveal": "जो मन में है, हमें बताएँ",
    "ctx.hide": "बिना व्यक्तिगत जानकारी के जारी रखें",

    /* ─── Landing sections ─── */
    "res.realQuestion": "हमें लगता है आपके मन का असली सवाल यह है",
    "features.eyebrow": "हम किन सवालों का जवाब देते हैं",
    "features.title.a": "वे सवाल जो रात को",
    "features.title.b": "आपको जागते रखते हैं।",
    "feature.career.title": "करियर की स्पष्टता",
    "feature.career.q": "क्या मुझे नौकरी बदलनी चाहिए?",
    "feature.relationship.title": "रिश्तों की सलाह",
    "feature.relationship.q": "हम एक-दूसरे से क्यों दूर होते जा रहे हैं?",
    "feature.finance.title": "आर्थिक निर्णय",
    "feature.finance.q": "क्या यह सही निवेश है?",
    "feature.emotion.title": "भावनात्मक स्वास्थ्य",
    "feature.emotion.q": "मुझे इतनी बेचैनी क्यों हो रही है?",
    "feature.spiritual.title": "आध्यात्मिक मार्गदर्शन",
    "feature.spiritual.q": "कौन-सा अभ्यास मेरे लिए सहायक होगा?",
    "feature.purpose.title": "जीवन का उद्देश्य",
    "feature.purpose.q": "मुझे असल में क्या बनाना है?",

    /* ─── Approach section ─── */
    "approach.eyebrow": "हमारा तरीका",
    "approach.title.a": "सिर्फ ज्योतिष नहीं।",
    "approach.title.b": "स्पष्टता की इंजीनियरिंग।",
    "approach.body": "मंत्रणा छह विधाओं को एक शांत, बुद्धिमान साथी में जोड़ता है — जो वह सुनने के लिए तैयार है जो आप ज़ोर से नहीं कह पाते, और वही जवाब देता है जिसकी आपको सबसे ज़्यादा ज़रूरत है।",
    "approach.item.1": "वैदिक ज्योतिष — समय और जीवन की लय",
    "approach.item.2": "अंकशास्त्र — भीतरी पैटर्न और झुकाव",
    "approach.item.3": "मनोविज्ञान — सोच का पूर्वाग्रह और भावनाएँ",
    "approach.item.4": "व्यवहार विज्ञान — आदतें और फैसले",
    "approach.item.5": "आध्यात्मिक समझ — अर्थ और गहराई",
    "approach.item.6": "AI निर्णय सहायता — स्पष्ट, व्यक्तिगत कदम",
    "approach.sample.eyebrow": "नमूना रीडिंग",
    "approach.sample.clarity": "स्पष्टता",
    "approach.sample.emotion.label": "भावनात्मक स्थिति",
    "approach.sample.emotion.body": "आप आत्मविश्वास से नहीं, अनिश्चितता से काम कर रहे हैं।",
    "approach.sample.blind.label": "छिपी हुई रुकावट",
    "approach.sample.blind.body": "असफलता का डर आपके सामने रखे विकल्पों को प्रभावित कर रहा है।",
    "approach.sample.action.label": "तुरंत उठाने का कदम",
    "approach.sample.action.body": "आज रात — सबसे बुरी स्थिति को विस्तार से लिखें। कल इसे फिर से पढ़ें।",

    /* ─── Growth / Clarity card & Simulator sections ─── */
    "growth.card.eyebrow": "क्लैरिटी कार्ड",
    "growth.card.title.a": "भारत में सबसे ज़्यादा साझा किया जाने वाला",
    "growth.card.title.b": "स्पष्टता का पल",
    "growth.card.title.c": "।",
    "growth.card.body": "हर रीडिंग एक सुंदर क्लैरिटी कार्ड बनाती है — आपके स्कोर, आपकी सबसे बड़ी अनदेखी, और सुझाया गया कदम — भेजने के लिए तैयार। रात 2 बजे परेशान दोस्त को, नौकरी के बारे में सोच रहे भाई-बहन को, या किसी भी ऐसे व्यक्ति को जिसे खुद को साफ़ सुनने की ज़रूरत है। साझा करना हमेशा मुफ़्त है।",
    "growth.card.stat.channels": "साझा करने के तरीके",
    "growth.card.stat.shares": "हर कार्ड की साझेदारियाँ",
    "growth.card.stat.free": "साझा करने का ख़र्च",
    "growth.card.cta": "मेरा क्लैरिटी कार्ड बनाएँ",
    "growth.sim.eyebrow": "जीवन निर्णय सिम्युलेटर",
    "growth.sim.title.a": "तीन संभावित रास्ते।",
    "growth.sim.title.b": "एक फैसला।",
    "growth.sim.body": "क्या आपको नौकरी बदलनी चाहिए? शहर बदलना चाहिए? शादी करनी चाहिए? तीन ईमानदार रास्ते देखिए — सुरक्षित, संतुलित, साहसी — हर एक की संतुष्टि की संभावना, और भावनात्मक, आर्थिक और रिश्तों पर असर के साथ।",
    "growth.sim.cta": "सिम्युलेटर आज़माएँ",

    /* ─── Pricing (landing) ─── */
    "pricing.eyebrow": "मूल्य",
    "pricing.title": "स्पष्टता से शुरुआत करें।",
    "pricing.subtitle": "हर व्यक्ति के लिए एक मुफ़्त रीडिंग। उसके बाद जितने सवाल पूछें, उतने का ही भुगतान करें।",
    "pricing.free.label": "मुफ़्त रीडिंग",
    "pricing.free.oneEach": "हर व्यक्ति के लिए एक",
    "pricing.free.item.1": "एक पूरी क्लैरिटी रीडिंग",
    "pricing.free.item.2": "आपके लिए बनाई गई कार्य योजना",
    "pricing.free.item.3": "साझा करने योग्य क्लैरिटी कार्ड",
    "pricing.free.cta": "मुफ़्त शुरू करें",
    "pricing.recommended": "अनुशंसित",
    "pricing.pack.oneQ": "1 सवाल",
    "pricing.pack.nQ": "{n} सवाल",
    "pricing.pack.item.1": "गहरी व्यक्तिगत सलाह",
    "pricing.pack.item.2": "निर्णय का विश्लेषण",
    "pricing.pack.item.3": "भावनात्मक और व्यवहार का मानचित्र",
    "pricing.pack.item.4": "प्रीमियम क्लैरिटी रिपोर्ट",
    "pricing.pack.getOne": "1 सवाल लें — ₹{price}",
    "pricing.pack.getN": "{n} सवाल लें — ₹{price}",
    "footer.byline": "स्पष्टता, आदर के साथ रची गई।",

    /* ─── Onboarding (Step 1) ─── */
    "onb.step1.eyebrow": "जन्म-विवरण",
    "onb.step1.title.a": "शुरुआत करते हैं",
    "onb.step1.title.b": "आपसे।",
    "onb.step1.subtitle": "आपकी जन्मकुंडली हमारी दृष्टि तय करती है। यह पूरी तरह गोपनीय है।",
    "onb.fullName": "पूरा नाम",
    "onb.fullName.placeholder": "आपका नाम",
    "onb.dob": "जन्म की तारीख",
    "onb.tob": "जन्म का समय",
    "onb.pob": "जन्म का स्थान",
    "onb.pob.placeholder": "शहर, देश",
    "onb.gender": "लिंग",
    "onb.gender.select": "चुनें",
    "onb.gender.male": "पुरुष",
    "onb.gender.female": "स्त्री",
    "onb.gender.other": "अन्य",
    "onb.gender.preferNot": "बताना नहीं चाहते",
    "onb.whatsapp": "व्हाट्सएप नंबर",
    "onb.whatsapp.forCode": "कोड के लिए",
    "onb.whatsapp.placeholder": "जैसे +91 98765 43210",
    "onb.whatsapp.send": "कोड भेजें",
    "onb.whatsapp.sending": "भेजा जा रहा है…",
    "onb.whatsapp.resend": "फिर से भेजें",
    "onb.whatsapp.enterOtp": "6-अंकों का कोड डालें",
    "onb.whatsapp.verify": "सत्यापित करें",
    "onb.whatsapp.verified": "व्हाट्सएप सत्यापित",
    "onb.whatsapp.demoCode": "डेमो मोड — कोड",
    "onb.step.phase": "चरण {n} / 3",
    "onb.errors.missingBirth": "कृपया सभी जन्म-विवरण भरें",
    "onb.errors.needCategory": "कृपया चुनें कि आपके मन में क्या चल रहा है",
    "onb.errors.answerAll": "कृपया हर सवाल का जवाब दें",

    /* ─── Onboarding (Step 2) ─── */
    "onb.step2.eyebrow": "आपके मन में क्या है",
    "onb.step2.title.a": "जीवन के किस हिस्से में",
    "onb.step2.title.b": "आपको स्पष्टता चाहिए?",
    "onb.step2.subtitle": "एक चुनें — हम आपकी रीडिंग उसी के अनुसार तैयार करेंगे।",
    "onb.returning.eyebrow": "फिर से स्वागत है",
    "onb.returning.body.a": "आपकी पिछली",
    "onb.returning.body.b": "रीडिंग हमारे पास है। क्या आप",
    "onb.returning.body.highlight": "60 सेकंड की छोटी जाँच",
    "onb.returning.body.c": "करना चाहेंगे?",
    "onb.returning.quick": "छोटी जाँच",
    "onb.returning.full": "पूरी रीडिंग",
    "cat.Career": "करियर",
    "cat.Relationships": "रिश्ते",
    "cat.Money": "पैसे",
    "cat.Family": "परिवार",
    "cat.Purpose": "उद्देश्य",
    "cat.Major Decision": "बड़ा फ़ैसला",

    /* ─── Onboarding (Step 3) ─── */
    "onb.step3.eyebrow": "5-सवालों का निर्णय प्रोफ़ाइल",
    "onb.step3.eyebrow.quick": "छोटी जाँच",
    "onb.step3.title": "पाँच सरल सवाल",
    "onb.step3.subtitle": "हर सवाल आपके एक अलग पहलू को समझने के लिए है — फैसले लेने का तरीका, भावनाएँ, जोखिम, प्रेरणा और भीतरी उलझन। सच्चाई से जवाब दें; कोई सही या ग़लत जवाब नहीं है।",
    "onb.step3.progress": "सवाल {i} / {n}",
    "onb.step3.answered": "{a} / {n} जवाब दिए",
    "onb.step3.answerMore": "{n} और सवाल का जवाब दें",
    "onb.step3.finish": "मेरा क्लैरिटी नक्शा दिखाएँ",

    /* ─── Processing ─── */
    "proc.eyebrow": "लाइव · आपकी ऊर्जा से जुड़ रहे हैं",
    "proc.title.a": "आपका",
    "proc.title.b": "क्लैरिटी नक्शा बन रहा है",
    "proc.personalize": "आपके लिए तैयार किया जा रहा है —",
    "proc.phase1.title": "आपके मन के फैसले लेने के तरीके को समझ रहे हैं",
    "proc.phase1.subtitle": "आपके जवाबों से आपकी निर्णय-शैली की पहचान बना रहे हैं",
    "proc.phase2.title": "ज्योतिष और अंकशास्त्र की गणना",
    "proc.phase2.subtitle": "जीवन-अंक, अभिव्यक्ति, वर्ष-अंक और वर्तमान ग्रह",
    "proc.phase3.title": "मनोविज्ञान, ज्योतिष और अंकशास्त्र को एक साथ जोड़ रहे हैं",
    "proc.phase3.subtitle": "मंत्रणा One तीनों संकेतों को एक रिपोर्ट में पिरो रहा है",
    "proc.status.now": "अभी",
    "proc.status.done": "हो गया",
    "proc.retry.notice": "फिर से कोशिश हो रही है — इंजन को एक पल के लिए रुकावट आई…",
    "proc.elapsed": "बीता · लाइव तैयार हो रहा है",
    "proc.phase.of": "चरण {i} / 3",
    "proc.footer.saved": "आपकी सारी जानकारी सुरक्षित है — कुछ भी नहीं खोया",
    "proc.err.smallTitle": "छोटी सी रुकावट",
    "proc.err.pauseTitle": "थोड़ी देर का विराम",
    "proc.err.back": "जानकारी पर वापस",
    "proc.numerology.lifePath": "जीवन-अंक",
    "proc.numerology.expression": "अभिव्यक्ति-अंक",
    "proc.numerology.personalYear": "वर्ष-अंक",

    /* ─── Error copy per code ─── */
    "err.llm_budget_exceeded.title": "हमारा AI साथी थोड़ी देर आराम कर रहा है",
    "err.llm_budget_exceeded.body": "इंजन ने आज की सीमा पूरी कर ली है। कुछ मिनटों में यह ठीक हो जाएगा — कृपया थोड़ी देर में फिर कोशिश करें।",
    "err.llm_quota_exceeded.title": "AI साथी साँस ले रहा है",
    "err.llm_quota_exceeded.body": "हमारी क्षमता एक पल के लिए भर गई है। एक मिनट रुककर फिर कोशिश करें — आपकी जानकारी सुरक्षित है।",
    "err.llm_auth_failed.title": "AI साथी अभी ऑफ़लाइन है",
    "err.llm_auth_failed.body": "इंजन जुड़ नहीं पाया। हमारी टीम को सूचना मिल गई है — कृपया कुछ मिनटों में फिर से कोशिश करें।",
    "err.llm_bad_json.title": "स्पष्टता से एक कदम दूर",
    "err.llm_bad_json.body": "रीडिंग ठीक से नहीं बनी। दूसरी कोशिश में यह अक्सर ठीक हो जाती है।",
    "err.llm_transient.title": "एक छोटी सी रुकावट",
    "err.llm_transient.body": "हमारा AI साथी पल भर के लिए व्यस्त था। नई कोशिश आमतौर पर सफल होती है।",
    "err.llm_not_configured.title": "सेटअप ज़रूरी है",
    "err.llm_not_configured.body": "AI साथी सर्वर पर तैयार नहीं है। कृपया सहायता से संपर्क करें।",
    "err.network.title": "कनेक्शन टूट गया",
    "err.network.body": "इंजन से संपर्क टूट गया है। अपना नेटवर्क जाँचें और फिर कोशिश करें।",
    "err.llm_unknown.title": "कुछ अनपेक्षित हुआ",
    "err.llm_unknown.body": "इंजन में असामान्य रुकावट आई है। फिर से कोशिश करने पर आमतौर पर यह ठीक हो जाता है — यदि नहीं, तो सहायता से संपर्क करें।",

    /* ─── Results ─── */
    "res.eyebrow": "आपकी क्लैरिटी रिपोर्ट",
    "res.dci": "निर्णय-स्पष्टता सूचकांक",
    "res.mindSays": "आपका मन क्या कह रहा है",
    "res.emotionsSay": "आपकी भावनाएँ क्या कह रही हैं",
    "res.astrologySuggests": "आपकी ज्योतिष क्या संकेत देती है",
    "res.numerologySuggests": "आपका अंकशास्त्र क्या दिखाता है",
    "res.agreement": "जहाँ सभी संकेत एक ही दिशा में हैं",
    "res.blindSpot": "सबसे बड़ी अनदेखी",
    "res.ifWait": "अगर आप रुकते हैं",
    "res.ifAct": "अगर आप कदम उठाते हैं",
    "res.action": "सुझाया गया कदम",
    "res.spiritual": "आध्यात्मिक अभ्यास",
    "res.spiritual.mantra": "मंत्र",
    "res.spiritual.temple": "मंदिर",
    "res.spiritual.meditation": "ध्यान",
    "res.spiritual.reflection": "चिंतन",
    "res.spiritual.why": "यह अभ्यास क्यों",
    "res.hiddenBlockers": "छिपी हुई रुकावटें",
    "res.confidence": "विश्वास-स्तर",
    "res.confidence.astro": "ज्योतिषीय",
    "res.confidence.num": "अंकशास्त्रीय",
    "res.confidence.behav": "व्यवहारजन्य",
    "res.confidence.overall": "समग्र",
    "res.dl.card": "क्लैरिटी कार्ड डाउनलोड करें",
    "res.share.card": "साझा करें",
    "res.askMantra": "मंत्रणा से पूछें",
    "res.compareLast": "पिछली रीडिंग से तुलना करें",
    "res.emotionalConflict": "भावनात्मक खिंचाव",
    "res.hiddenFear": "छिपा हुआ डर",
    "res.decisionTension": "निर्णय का तनाव",
    "res.hero.greeting": "{name}, हमने जो देखा वो यह है।",
    "res.loadError": "आपकी रीडिंग नहीं खुल पाई।",
    "res.loading": "आपकी रीडिंग लोड हो रही है…",
    "res.notAvailable": "उपलब्ध नहीं।",

    /* ─── Radar axis names ─── */
    "radar.decision_confidence": "निर्णय का आत्मविश्वास",
    "radar.emotional_clarity": "भावनात्मक स्पष्टता",
    "radar.risk_readiness": "जोखिम की तैयारी",
    "radar.purpose_alignment": "उद्देश्य का तालमेल",
    "radar.self_trust": "आत्म-विश्वास",

    /* ─── Paywall ─── */
    "pw.title": "आप अपनी वर्तमान क्लैरिटी यात्रा के अंत तक पहुँच गए हैं",
    "pw.subtitle": "गहरी व्यक्तिगत सलाह के साथ आगे बढ़ें।",
    "pw.premiumRequired": "प्रीमियम आवश्यक",
    "pw.pack.1q": "1 अतिरिक्त क्लैरिटी सवाल",
    "pw.pack.3q": "3 अतिरिक्त क्लैरिटी सवाल",
    "pw.pack.get": "{label} लें — ₹{price}",
    "pw.previous.eyebrow": "आपकी पिछली रीडिंग",
    "pw.previous.blurred": "बाकी देखने के लिए और स्पष्टता अनलॉक करें",
    "pw.teaser.90day": "{name} की 90-दिन की योजना",
    "pw.teaser.90day.you": "आपकी 90-दिन की योजना",
    "pw.teaser.practice": "आपका व्यक्तिगत अभ्यास",
    "pw.teaser.nextStep": "आपका अगला निर्णायक कदम",
    "pw.teaser.blindSpot": "आपकी पूरी अनदेखी",
    "pw.teaser.unlock": "क्लैरिटी पैक से अनलॉक करें",
    "pw.footer.note": "आपकी मुफ़्त क्लैरिटी रीडिंग और अनुवर्ती सवाल पूरे हो चुके हैं। गहरी सलाह के लिए एक क्लैरिटी पैक चुनें।",
    "pw.demo.note": "डेमो चेकआउट · पेमेंट इंटीग्रेशन तैयार है",
    "pw.processing": "प्रोसेस हो रहा है…",

    /* ─── Clarity Card (share card) ─── */
    "card.section.eyebrow": "मेरा क्लैरिटी कार्ड",
    "card.section.title.a": "आज के सच की",
    "card.section.title.b": "एक निशानी।",
    "card.section.subtitle": "इसे साझा कीजिए। जिसे आप यह भेजेंगे, अभी उसे इसकी सबसे ज़्यादा ज़रूरत है।",
    "card.header": "क्लैरिटी कार्ड",
    "card.for": "के लिए",
    "card.archetype": "मूल स्वरूप",
    "card.clarityScore": "क्लैरिटी स्कोर",
    "card.emotional": "भावनात्मक",
    "card.relationships": "रिश्ते",
    "card.blindSpot": "सबसे बड़ी अनदेखी",
    "card.action": "सुझाया गया कदम",
    "card.fallbackAction": "रुकें। साँस लें। सोच-समझकर चुनें।",
    "card.share.whatsapp": "व्हाट्सएप",
    "card.share.instagram": "इंस्टाग्राम",
    "card.share.download": "कार्ड डाउनलोड करें",
    "card.share.saved": "कार्ड आपके डिवाइस में सेव हो गया",
    "card.share.downloadFailed": "कार्ड डाउनलोड नहीं हो पाया",
    "card.share.instaSaved": "कार्ड सेव हो गया और कैप्शन कॉपी हो गया — पोस्ट करने के लिए इंस्टाग्राम खोलें",
    "card.share.privacy": "हर साझेदारी में आपकी अपनी लिंक होती है · हम आपकी गोपनीयता का सम्मान करते हैं",
    "card.caption": "मेरा मंत्रणा One क्लैरिटी कार्ड 🪔\nक्लैरिटी स्कोर: {clarity}/100\nभावनात्मक: {emotional}/100 · रिश्ते: {relationship}/100\n\nसबसे बड़ी अनदेखी: {blindSpot}\nअगला कदम: {action}\n\nअपनी स्पष्टता पाएँ → {url}",

    /* ─── Floating Clarity Card (landing hero) ─── */
    "float.header": "क्लैरिटी रिपोर्ट",
    "float.blindSpot": "मुख्य अनदेखी",
    "float.focus": "सुझाया गया ध्यान",
    "float.timing": "समय",
    "float.confidence": "विश्वास",

    /* ─── Chat (Mantrana) ─── */
    "chat.title": "मंत्रणा",
    "chat.subtitle": "आपका स्पष्टता साथी",
    "chat.placeholder": "मंत्रणा से कुछ भी पूछें…",
    "chat.send": "भेजें",
    "chat.welcome": "मैं मंत्रणा हूँ — आपकी व्यक्तिगत स्पष्टता साथी। अपने मन का सवाल या उलझन मेरे साथ साझा करें। मैं ध्यान से सुनूँगी और वही जवाब दूँगी जिसकी आपको सबसे ज़्यादा ज़रूरत है।",
    "chat.locked": "आपने अपना मुफ़्त अनुवर्ती सवाल इस्तेमाल कर लिया है। बातचीत जारी रखने के लिए और स्पष्टता अनलॉक करें।",
    "chat.thinking": "सोच रही है…",
    "chat.status.alwaysHere": "हमेशा साथ",
    "chat.status.premium": "प्रीमियम ज़रूरी",
    "chat.status.remaining": "बचे हुए सवाल: {n}",
    "chat.balanceHint": "{n} भुगतान किए हुए सवाल बचे हैं",
    "chat.unlockCta": "और सवाल अनलॉक करें",
    "chat.upsell.eyebrow": "मुफ़्त सत्र पूरा हुआ",
    "chat.upsell.body": "यह आपकी एक मुफ़्त क्लैरिटी रीडिंग थी। एक पेड पैक के साथ आगे बढ़ें — हर सवाल के लिए गहरी सलाह, निर्णय का विश्लेषण और प्रीमियम क्लैरिटी रिपोर्ट।",
    "chat.upsell.cta": "एक क्लैरिटी पैक चुनें",
    "chat.suggest.career.label": "करियर की उलझन",
    "chat.suggest.career.q": "क्या मुझे करियर बदलना चाहिए?",
    "chat.suggest.relationship.label": "रिश्तों की दुविधा",
    "chat.suggest.relationship.q": "क्या मुझे इस रिश्ते को आगे बढ़ाना चाहिए?",
    "chat.suggest.finance.label": "आर्थिक निर्णय",
    "chat.suggest.finance.q": "इस आर्थिक फैसले के बारे में मैं साफ़ कैसे सोचूँ?",
    "chat.suggest.purpose.label": "जीवन का उद्देश्य",
    "chat.suggest.purpose.q": "मैं अटका हुआ क्यों महसूस कर रहा हूँ — मेरी प्रगति में क्या रुकावट है?",
    "chat.paywall.title": "आपकी मुफ़्त क्लैरिटी रीडिंग पूरी हुई",
    "chat.paywall.subtitle": "गहरी व्यक्तिगत सलाह के साथ आगे बढ़ें।",
    "chat.paywall.getShort": "{n} लें",
    "chat.paywall.oneQ": "सवाल",
    "chat.paywall.nQ": "सवाल",

    /* ─── Simulator ─── */
    "sim.eyebrow": "जीवन निर्णय सिम्युलेटर",
    "sim.title.a": "तीन संभावित रास्ते देखें",
    "sim.title.b": "इससे पहले कि आप चुनें",
    "sim.title.italic": "एक।",
    "sim.subtitle": "कोई भी फ़ैसला पूछें — करियर, प्रेम, पैसे, जगह। मंत्रणा तीन ईमानदार रास्ते तैयार करती है ताकि आप निर्णय से पहले हर एक को महसूस कर सकें।",
    "sim.placeholder": "पूछें: क्या मुझे…",
    "sim.button.simulate": "सिम्युलेट करें",
    "sim.button.simulating": "तैयार हो रहा है",
    "sim.err.short": "पहले एक असली सवाल लिखें",
    "sim.err.fail": "सिम्युलेट नहीं हो पाया। एक बार और कोशिश करें।",
    "sim.suggest.job": "क्या मुझे नौकरी बदलनी चाहिए?",
    "sim.suggest.business": "क्या मुझे अपना व्यवसाय शुरू करना चाहिए?",
    "sim.suggest.move": "क्या मुझे शहर बदलना चाहिए?",
    "sim.suggest.marry": "क्या मुझे शादी करनी चाहिए?",
    "sim.yourQuestion": "आपका सवाल",
    "sim.riskLabel": "{level} जोखिम",
    "sim.risk.low": "कम",
    "sim.risk.medium": "मध्यम",
    "sim.risk.high": "अधिक",
    "sim.probability": "संतुष्टि की संभावना",
    "sim.of100": "/ 100",
    "sim.impact.emotional": "भावनात्मक असर",
    "sim.impact.financial": "आर्थिक असर",
    "sim.impact.relationship": "रिश्तों पर असर",
    "sim.recommended": "सुझाया गया कदम",
    "sim.mantraTake": "मंत्रणा का दृष्टिकोण",
    "sim.getReading": "पूरी रीडिंग लें",
    "sim.askAnother": "एक और सवाल पूछें",

    /* ─── Purchase / toast ─── */
    "purchase.success": "खरीदारी सफल — {n} अतिरिक्त सवाल अनलॉक हो गए",
    "purchase.unlocked": "अनलॉक हो गया — {n} सवाल बचे हैं",
    "purchase.failed": "भुगतान असफल। कृपया फिर से कोशिश करें।",
    "purchase.verifyFailed": "भुगतान का सत्यापन नहीं हो पाया। कृपया सहायता से संपर्क करें।",

    /* ─── Paywall pack features (localized bullets per pack) ─── */
    "pw.pack.pack_1.f1": "एक अतिरिक्त सवाल",
    "pw.pack.pack_1.f2": "आपके लिए तैयार क्लैरिटी सलाह",
    "pw.pack.pack_1.f3": "निर्णय का विश्लेषण",
    "pw.pack.pack_1.f4": "सुझाए गए कदम",
    "pw.pack.pack_3.f1": "तीन अतिरिक्त सवाल",
    "pw.pack.pack_3.f2": "गहरा क्लैरिटी विश्लेषण",
    "pw.pack.pack_3.f3": "व्यवहार की समझ",
    "pw.pack.pack_3.f4": "कदमों की पूरी योजना",
    "pw.pack.pack_3.f5": "प्रीमियम मार्गदर्शन",
    "pw.pack.default.f1": "आपके लिए तैयार क्लैरिटी सलाह",
    "pw.pack.default.f2": "निर्णय का विश्लेषण",
    "pw.pack.default.f3": "भावनात्मक और व्यवहार का मानचित्र",
    "pw.pack.default.f4": "प्रीमियम क्लैरिटी रिपोर्ट",
  },
};

/* ---------------------------------------------------------------- *
 * Helpers
 * ---------------------------------------------------------------- */
export function tRaw(lang, key, params) {
  const dict = STRINGS[lang] || STRINGS.en;
  let str = dict[key];
  if (str == null) str = STRINGS.en[key];
  if (str == null) str = key;
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      str = str.replaceAll(`{${k}}`, String(v));
    }
  }
  return str;
}

export function readStoredLang() {
  if (typeof window === "undefined") return "en";
  try {
    const v = localStorage.getItem(LS_KEY);
    if (v === "hi" || v === "en") return v;
  } catch (_) {}
  return null; // null = user hasn't chosen yet
}

export function writeStoredLang(lang) {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(LS_KEY, lang);
    document.documentElement.setAttribute("lang", lang);
  } catch (_) {}
}

/* ---------------------------------------------------------------- *
 * Context
 * ---------------------------------------------------------------- */
const LangContext = createContext(null);

export function LangProvider({ children }) {
  const [lang, setLangState] = useState(() => readStoredLang() || "en");

  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.setAttribute("lang", lang);
    }
  }, [lang]);

  const setLang = useCallback((next) => {
    if (next !== "en" && next !== "hi") return;
    writeStoredLang(next);
    setLangState(next);
  }, []);

  const t = useCallback((key, params) => tRaw(lang, key, params), [lang]);

  const value = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t]);
  return <LangContext.Provider value={value}>{children}</LangContext.Provider>;
}

export function useLang() {
  const ctx = useContext(LangContext);
  if (!ctx) {
    // Safe fallback when accidentally rendered outside provider (e.g. tests).
    return {
      lang: "en",
      setLang: () => {},
      t: (k, p) => tRaw("en", k, p),
    };
  }
  return ctx;
}
